/**
 * Custom authentication hooks
 *
 * Provides unified hooks that work with both mock and OIDC authentication providers.
 * Abstracts the authentication provider implementation from components.
 *
 * Provider-agnostic: Works with Cognito, Entra, or any OIDC provider.
 */

import { useAuth as useOidcAuth } from 'react-oidc-context';
import type { User } from 'oidc-client-ts';

import { mapClaimsForProvider, type CanonicalUserClaims } from './claim-mapping';
import { getAuthProvider, buildLogoutArgs, buildOidcConfig } from './providers/oidc';
import { logAuthEvent, getStoredCorrelationId, generateCorrelationId } from './logging';
import { getConfig } from '../config';

/**
 * Authentication state and methods
 */
export interface AuthHook {
  /** Whether user is currently authenticated */
  isAuthenticated: boolean;
  /** Whether authentication state is being determined */
  isLoading: boolean;
  /** Current authenticated user */
  user: User | null | undefined;
  /** Authentication error, if any */
  error: Error | undefined;
  /** Initiate sign in flow */
  signinRedirect: () => Promise<void>;
  /** Initiate sign out flow */
  signoutRedirect: () => Promise<void>;
  /** Remove user session */
  removeUser: () => Promise<void>;
  /** Silent token renewal */
  signinSilent: () => Promise<User | null>;
}

/**
 * Hook for accessing authentication state and methods
 *
 * Works with both MockAuthProvider and OidcAuthProvider.
 * Provides a consistent API regardless of the authentication provider.
 *
 * This hook directly re-exports useAuth from react-oidc-context,
 * which works for both mock and OIDC providers because they both
 * provide the same context interface.
 *
 * @returns Authentication state and methods
 */
export function useAuth(): AuthHook {
  // This works for both providers because:
  // - OidcAuthProvider wraps react-oidc-context's AuthProvider
  // - MockAuthProvider uses the same context structure
  const auth = useOidcAuth();

  return {
    isAuthenticated: auth.isAuthenticated,
    isLoading: auth.isLoading,
    user: auth.user,
    error: auth.error,
    signinRedirect: auth.signinRedirect,
    signoutRedirect: auth.signoutRedirect,
    removeUser: auth.removeUser,
    signinSilent: auth.signinSilent,
  };
}

/**
 * User profile information (provider-agnostic)
 */
export interface UserProfile {
  /** User's email address */
  email: string;
  /** User's display name */
  name: string;
  /** User's unique identifier (provider-agnostic) */
  sub: string;
  /** User's groups/roles (normalized from provider-specific claims) */
  groups: string[];
  /** Authentication provider: 'cognito', 'entra', 'local' */
  provider: string;
  /** Preferred username (may be email) */
  preferredUsername?: string;
}

/**
 * Hook for accessing current user profile
 *
 * Extracts and normalizes user profile fields from the authentication user object.
 * Uses claim mapping to handle provider-specific claim names.
 * Returns null if user is not authenticated.
 *
 * @returns User profile or null
 */
export function useUser(): UserProfile | null {
  const auth = useAuth();

  if (!auth.isAuthenticated || !auth.user?.profile) {
    return null;
  }

  const profile = auth.user.profile;
  const provider = getAuthProvider();

  // Map claims using provider-specific mapping
  const canonicalClaims = mapClaimsForProvider(profile as Record<string, unknown>, provider);

  return {
    email: canonicalClaims.email || (profile.preferred_username as string) || '',
    name: canonicalClaims.name || canonicalClaims.email || 'User',
    sub: canonicalClaims.sub || profile.sub || '',
    groups: canonicalClaims.groups,
    provider: canonicalClaims.provider,
    preferredUsername: profile.preferred_username as string | undefined,
  };
}

/**
 * Hook for accessing canonical user claims
 *
 * Returns fully normalized claims that work across all providers.
 *
 * @returns Canonical user claims or null
 */
export function useCanonicalClaims(): CanonicalUserClaims | null {
  const auth = useAuth();

  if (!auth.isAuthenticated || !auth.user?.profile) {
    return null;
  }

  const profile = auth.user.profile;
  const provider = getAuthProvider();

  return mapClaimsForProvider(profile as Record<string, unknown>, provider);
}

/**
 * Hook for checking if user has a specific role or permission
 *
 * Uses normalized groups from claim mapping (provider-agnostic).
 * Returns false if user is not authenticated or groups are not available.
 *
 * @param role - Role to check for
 * @returns Whether user has the specified role
 */
export function useHasRole(role: string): boolean {
  const claims = useCanonicalClaims();

  if (!claims) {
    return false;
  }

  return claims.groups.includes(role);
}

/**
 * Hook for getting user's access token
 *
 * Useful for making authenticated API requests.
 * Returns null if user is not authenticated.
 *
 * @returns Access token or null
 */
export function useAccessToken(): string | null {
  const auth = useAuth();

  if (!auth.isAuthenticated || !auth.user) {
    return null;
  }

  return auth.user.access_token || null;
}

/**
 * Provider-specific logout hook result
 */
export interface ProviderLogoutHook {
  /** Initiate provider-specific sign out flow */
  signoutRedirect: () => Promise<void>;
}

/**
 * Hook for provider-specific logout
 *
 * Handles provider-specific logout URL requirements:
 * - AWS Cognito: Requires manual redirect to /logout endpoint with client_id and logout_uri
 * - Entra ID: Uses standard OIDC id_token_hint
 * - Standard OIDC: Uses id_token_hint and post_logout_redirect_uri
 *
 * NOTE: AWS Cognito does NOT follow standard OIDC logout. The oidc-client-ts library
 * uses the discovery document's end_session_endpoint, which doesn't work correctly
 * with Cognito. For Cognito, we must manually construct the logout URL and redirect.
 *
 * @returns Provider logout hook with signoutRedirect function
 */
export function useProviderLogout(): ProviderLogoutHook {
  const auth = useOidcAuth();
  const provider = getAuthProvider();
  const oidcConfig = buildOidcConfig();

  const signoutRedirect = async (): Promise<void> => {
    const correlationId = getStoredCorrelationId() || generateCorrelationId();

    // Log logout initiation
    logAuthEvent('logout_initiated', 'User initiated sign-out flow', {
      correlationId,
      provider
    });

    // For local/mock auth, just remove the user
    if (!oidcConfig) {
      await auth.removeUser();
      logAuthEvent('logout_complete', 'User signed out (mock)', {
        correlationId,
        provider
      });
      return;
    }

    // For Cognito, we must manually redirect to the /logout endpoint
    // The OIDC library's signoutRedirect uses the wrong endpoint from discovery
    if (provider === 'cognito') {
      // Build Cognito logout URL manually
      // Cognito logout endpoint: https://{domain}/logout?client_id={id}&redirect_uri={uri}&response_type=code&scope=...
      // Using redirect_uri (not logout_uri) because redirect_uri uses "Allowed callback URLs" which are already configured
      // The authority URL is like: https://cognito-idp.us-east-1.amazonaws.com/us-east-1_xxx
      // But the logout endpoint is on the hosted UI domain, not the authority

      // Get Cognito domain from the runtime config (oidc.cognitoDomain).
      let cognitoDomain: string | undefined;
      try {
        cognitoDomain = getConfig().oidc?.cognitoDomain;
      } catch {
        // Config loading failed — fall back to deriving it from the discovery document below.
        cognitoDomain = undefined;
      }

      // If no domain configured, try to derive from OIDC discovery document
      if (!cognitoDomain && oidcConfig.authority) {
        try {
          const discoveryUrl = `${oidcConfig.authority}/.well-known/openid-configuration`;
          const response = await fetch(discoveryUrl);
          if (response.ok) {
            const metadata = await response.json();
            // Extract domain from token_endpoint (e.g., https://auth.ftidev.link/oauth2/token)
            if (metadata.token_endpoint) {
              const tokenUrl = new URL(metadata.token_endpoint);
              cognitoDomain = tokenUrl.hostname;
              console.log(`[AUTH] Derived Cognito domain from discovery: ${cognitoDomain}`);
            }
          }
        } catch (fetchError) {
          console.warn('[AUTH] Failed to fetch OIDC discovery for logout domain:', fetchError);
        }
      }

      if (cognitoDomain) {
        // Handle both cases: domain with or without protocol prefix
        // Remove protocol if present (e.g., "https://auth.ftidev.link" -> "auth.ftidev.link")
        cognitoDomain = cognitoDomain.replace(/^https?:\/\//, '');

        // AWS Cognito logout has two modes:
        // 1. logout_uri: Redirects to a custom page (must be in "Allowed sign-out URLs")
        // 2. redirect_uri: Redirects to login page to re-authenticate (uses "Allowed callback URLs")
        //
        // Since we want to redirect to login after logout, use redirect_uri which triggers
        // the authorization flow. The redirect_uri must be in "Allowed callback URLs".
        const logoutUrl = new URL(`https://${cognitoDomain}/logout`);
        logoutUrl.searchParams.set('client_id', oidcConfig.clientId);
        logoutUrl.searchParams.set('redirect_uri', oidcConfig.redirectUri);
        logoutUrl.searchParams.set('response_type', 'code');
        logoutUrl.searchParams.set('scope', oidcConfig.scope || 'openid email profile');

        logAuthEvent('logout_redirect', 'Redirecting to Cognito logout', {
          correlationId,
          provider
        });

        // IMPORTANT: Redirect to Cognito FIRST to clear their session cookie.
        // Do NOT call removeUser() before this redirect - it will cause AuthGate
        // to see the user as unauthenticated and trigger a login redirect before
        // this window.location.href can execute. The local session will be cleared
        // when the user returns from Cognito logout (AuthGate will then auto-redirect
        // to login since the Cognito session is now invalidated).
        window.location.href = logoutUrl.toString();
        return;
      }

      // Fallback: no Cognito domain configured - just clear local session
      // This won't clear the Cognito session cookie, so user may auto-login again
      await auth.removeUser();
      logAuthEvent('logout_complete', 'User signed out (local session only - no Cognito domain configured)', {
        correlationId,
        provider
      });
      window.location.href = oidcConfig.postLogoutRedirectUri;
      return;
    }

    // For other providers (Entra, standard OIDC), use the library's logout
    const logoutArgs = buildLogoutArgs(
      provider,
      oidcConfig.clientId,
      oidcConfig.postLogoutRedirectUri,
      auth.user?.id_token
    );

    await auth.signoutRedirect(logoutArgs);
  };

  return { signoutRedirect };
}
